import { PLATFORM_BASE_URL } from "../platform/config";
import {
  Badge,
  Box,
  Button,
  Flex,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import React from "react";
import {
  OktonDataGrid,
  OktonDataGridColumn,
  OktonErrorState,
  OktonPageHeader,
  OktonPanel,
  useOktonAuth,
  OktonInput,
  formatDateTimeSeconds as formatDate,
} from "@okton/platform-ui";

interface AuditRow {
  id: string;
  subjectId?: string;
  applicationId?: string;
  action: string;
  context: Record<string, unknown>;
  createdAt: string;
  userName?: string;
  userEmail?: string;
}

export const AuditPage: React.FC = () => {
  const { accessToken } = useOktonAuth();
  const [rows, setRows] = React.useState<AuditRow[]>([]);
  const [application, setApplication] = React.useState("");
  const [action, setAction] = React.useState("");
  const [user, setUser] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({ limit: "300" });
      if (application.trim()) params.set("application", application.trim());
      if (action.trim()) params.set("action", action.trim());
      if (user.trim()) params.set("user", user.trim());

      const response = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/audit?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(
          payload.message || "Não foi possível carregar a auditoria.",
        );
      setRows(payload.data || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao carregar auditoria.",
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, application, action, user]);

  React.useEffect(() => {
    load();
  }, [accessToken]);

  const columns = React.useMemo<OktonDataGridColumn<AuditRow>[]>(
    () => [
      { key: "id", header: "ID", value: (row) => row.id },
      {
        key: "date",
        header: "Data",
        render: (row) => (
          <Text whiteSpace="nowrap">{formatDate(row.createdAt)}</Text>
        ),
      },
      {
        key: "user",
        header: "Usuário",
        render: (row) => (
          <Box>
            <Text fontWeight="600">
              {row.userName || row.userEmail || "Usuário"}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {row.userEmail || row.subjectId || "-"}
            </Text>
          </Box>
        ),
      },
      {
        key: "system",
        header: "Sistema",
        value: (row) => row.applicationId || "-",
      },
      {
        key: "action",
        header: "Ação",
        render: (row) => <Badge colorScheme="purple">{row.action}</Badge>,
      },
      {
        key: "context",
        header: "Contexto",
        render: (row) => (
          <Box
            as="pre"
            fontSize="xs"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            m="0"
            maxW="520px"
          >
            {JSON.stringify(row.context || {}, null, 2)}
          </Box>
        ),
      },
    ],
    [],
  );

  function clearFilters(): void {
    setApplication("");
    setAction("");
    setUser("");
    setTimeout(() => load(), 0);
  }

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 16]} w="100%">
      <OktonPageHeader
        title="Auditoria"
        subtitle="Rastreabilidade detalhada das ações administrativas e operacionais."
      />

      <OktonPanel style={{ marginBottom: 24 }}>
        <SimpleGrid columns={[1, 3]} spacing="3">
          <OktonInput
            name="audit-application"
            placeholder="Sistema (ex.: sso-admin)"
            value={application}
            onChange={(e) => setApplication(e.target.value)}
          />
          <OktonInput
            name="audit-action"
            placeholder="Ação (ex.: billing.pix)"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          />
          <OktonInput
            name="audit-user"
            placeholder="Usuário, e-mail ou subject"
            value={user}
            onChange={(e) => setUser(e.target.value)}
          />
        </SimpleGrid>
        <Flex mt="3" gap="3">
          <Button colorScheme="blue" size="sm" onClick={load}>
            Filtrar
          </Button>
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Limpar
          </Button>
        </Flex>
      </OktonPanel>

      {error ? (
        <OktonErrorState
          title="Não foi possível carregar a auditoria"
          description={error}
          minH="180px"
        />
      ) : (
        <OktonDataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          emptyMessage="Nenhum registro encontrado. Ajuste os filtros ou aguarde novos eventos de auditoria."
          hidePagination
        />
      )}
    </Box>
  );
};
